import React, { useState } from "react";
import "./CuppingGuideWidget.css";

const ScaAffectiveGuideWidget = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Per SCA Standard 102-2024: Affective Assessment, §5.1-5.3
  const categories = [
    {
      id: "fragrance",
      name: "Fragrance (Step 1 — dry grounds)",
      shortDescription: "Impression of quality of dry-ground fragrance",
      whatToNote:
        "Rate your subjective impression of quality. Does the fragrance signal a coffee you'd want to drink? Avoid pure description — justify your judgement.",
      example:
        '"Fragrance 7 — moderately high quality impression, rich berry sweetness that makes me excited to brew"',
    },
    {
      id: "aroma",
      name: "Aroma (Step 2 — after brewing)",
      shortDescription: "Impression of quality of wet aroma",
      whatToNote:
        "Assess after hot water is added. Has the impression of quality improved or declined? Note if off-aromas lower your impression.",
      example:
        '"Aroma 6 — slightly high quality, citrus and caramel develop but a faint grassy note detracts"',
    },
    {
      id: "flavor",
      name: "Flavor (Step 3 — while slurping)",
      shortDescription: "Impression of quality of the main flavor",
      whatToNote:
        "Rate as you slurp several times. Is this a flavor you'd seek out? Don't just list notes — explain why the flavor is high or low quality to you.",
      example:
        '"Flavor 7 — moderately high quality, bright stone fruit and chocolate work together beautifully"',
    },
    {
      id: "aftertaste",
      name: "Aftertaste (Step 3 — finish)",
      shortDescription: "Impression of quality of the lingering finish",
      whatToNote:
        "Does the finish leave you wanting another sip or reaching for water? Rate quality, not just length. A long harsh finish is low quality.",
      example:
        '"Aftertaste 5 — neither high nor low, cocoa lingers but finish is slightly short and drying"',
    },
    {
      id: "acidity",
      name: "Acidity (Step 3 — brightness)",
      shortDescription: "Impression of quality of the acidity",
      whatToNote:
        "Is the acidity pleasant and integrated (high quality) or harsh and dominating (low quality)? Bright/crisp = higher impression; sour/vinegary = lower.",
      example:
        '"Acidity 8 — very high quality, crisp malic acidity like a fresh apple, beautifully balanced"',
    },
    {
      id: "sweetness",
      name: "Sweetness (Step 3 — perceived sweetness)",
      shortDescription: "Impression of quality of perceived sweetness",
      whatToNote:
        "How does sweetness contribute to your overall impression? Does it round out the cup or is it absent/cloying? Note its role in harmony.",
      example:
        '"Sweetness 7 — moderately high quality, honey-like sweetness softens the bright acidity perfectly"',
    },
    {
      id: "mouthfeel",
      name: "Mouthfeel (Step 3 — body & texture)",
      shortDescription: "Impression of quality of body and tactile sensation",
      whatToNote:
        "Does the body feel pleasant and appropriate? Neither thin/watery (low) nor heavy/muddy (low). Note coating, astringency, creaminess.",
      example:
        '"Mouthfeel 6 — slightly high quality, medium body with smooth coating texture"',
    },
    {
      id: "overall",
      name: "Overall (Step 3 — holistic judgement)",
      shortDescription: "Final impression of quality across all attributes",
      whatToNote:
        "Your holistic judgement. Does the coffee work as a whole? This is not an average — it's your subjective final verdict on quality.",
      example:
        '"Overall 7 — moderately high quality, great acidity-sweetness balance makes this highly drinkable"',
    },
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="cupping-guide-widget">
      <button
        className="guide-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="guide-title">SCA Affective Assessment Guide</span>
        <span className="expand-icon">{isExpanded ? "−" : "+"}</span>
      </button>

      {isExpanded && (
        <div className="guide-content">
          <div className="scoring-scale">
            <h4>9-Point Impression of Quality Scale (§5.2)</h4>
            <div className="scale-items">
              <span>9 Extremely High</span>
              <span>8 Very High</span>
              <span>7 Moderately High</span>
              <span>6 Slightly High</span>
              <span>5 Neither High Nor Low</span>
              <span>4 Slightly Low</span>
              <span>3 Moderately Low</span>
              <span>2 Very Low</span>
              <span>1 Extremely Low</span>
            </div>
            <p
              style={{
                marginTop: "var(--space-sm)",
                fontSize: "13px",
                color: "var(--text-muted)",
                lineHeight: "1.5",
              }}
            >
              This is an <strong>affective</strong> (subjective) scale, not a
              descriptive grade. You are rating your personal impression of
              quality — your honest, intuitive judgement of whether the coffee
              is desirable or not. Authenticity is valued over conformity
              (§5.2).
            </p>
          </div>

          <div className="categories-grid">
            {categories.map((category) => (
              <div key={category.id} className="category-item">
                <button
                  className="category-header"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="category-info">
                    <h5>{category.name}</h5>
                    <p className="category-short">
                      {category.shortDescription}
                    </p>
                  </div>
                  <span className="category-expand">
                    {expandedCategory === category.id ? "−" : "+"}
                  </span>
                </button>

                {expandedCategory === category.id && (
                  <div className="category-details">
                    <div className="detail-section">
                      <strong>How to rate:</strong>
                      <p>{category.whatToNote}</p>
                    </div>
                    <div className="detail-section">
                      <strong>Example (affective judgement):</strong>
                      <p className="example-text">{category.example}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div
            className="scoring-scale"
            style={{ marginTop: "var(--space-md)" }}
          >
            <h4>Cup Uniformity &amp; Defects (§5.4)</h4>
            <div
              style={{
                fontSize: "13px",
                color: "var(--text-muted)",
                lineHeight: "1.5",
              }}
            >
              <p style={{ margin: "0 0 var(--space-sm) 0" }}>
                <strong>Non-uniform cups (0–5):</strong> Cups with{" "}
                <em>qualitatively</em> different characteristics. Only count
                when a distinctly different attribute is present or absent — not
                mere intensity differences. Brewing errors should not affect
                uniformity.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Defective cups (0–5):</strong> Cups with a recognized
                sensory defect. Defect types: <strong>potato</strong>,{" "}
                <strong>moldy</strong>, or <strong>phenolic</strong> only. Must
                identify the specific defect type — avoid catch-all terms. All
                defective cups are also non-uniform (unless the defect is even
                across all cups).
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScaAffectiveGuideWidget;
